/**
 * Port of astro_calculations/astrology/static_predictions.py — the LEGACY
 * prediction engine that the legacy fallback scorer (legacy_engine.score_birth_time
 * → get_predictions) drives. Not part of the live lib/ scoring path.
 *
 * The YAML it loads (static_astrology_config.yaml) is translated to a TS literal
 * in static_config_data.ts; everything else is a line-by-line port.
 *
 * Port divergence: Python's get_predictions can fall back to reading
 * config/birth.json from disk when no override_birth_info is given. File I/O does
 * not exist in the browser, so the TS port REQUIRES override_birth_info — the only
 * caller in the ported closure (score_birth_time) always passes it.
 */

import { pymod, pythonRound } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import { combine, dateOnly, diffSeconds, plainDateTime } from "../lib/datetime";
import { get_drishti_strength } from "./helpers";
import { TransitData, get_divisional_position, type TransitChartData } from "./transit_data";
import { STATIC_ASTRO_CONFIG } from "./static_config_data";

// ---------- config loading (YAML‑driven) ----------

const _ASTRO_CONFIG = STATIC_ASTRO_CONFIG;

// NEW: Load from YAML
export const RULERSHIPS: Record<number, string> = Object.fromEntries(
  Object.entries(_ASTRO_CONFIG["rulerships"]).map(([k, v]) => [Number(k), v as string])
);
export const TRANSIT_WEIGHTS_BASE: Record<string, number> = _ASTRO_CONFIG["transit_weights"]["planet_base"];
export const TRANSIT_WEIGHTS_MULTIPLIERS: Record<string, number> =
  _ASTRO_CONFIG["transit_weights"]["multipliers"];
export const DASHA_OFFSETS: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["dasha_influences"]["d1_offsets"];
export const DIVISIONAL_DASHA_OFFSETS: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["dasha_influences"]["divisional_offsets"];
export const ANTARDASHA_MULTIPLIER: number = _ASTRO_CONFIG["dasha_influences"]["antardasha_multiplier"];
export const PROFECTION_EMPHASIS: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["profection_emphasis"]["d1_boosts"];
export const DIVISIONAL_PROFECTION_EMPHASIS: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["profection_emphasis"]["divisional_boosts"];
export const HOUSE_INFLUENCES: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["house_influences"]["d1_weights"];
export const DIVISIONAL_HOUSE_INFLUENCES: Record<string, Record<string, number>> =
  _ASTRO_CONFIG["house_influences"]["divisional_weights"];
export const STRENGTH_MODIFIER_RANGE: number = _ASTRO_CONFIG["house_influences"]["strength_modifier_range"];
export const EVENT_DETECTION_CFG: Record<string, any> = _ASTRO_CONFIG["event_detection"];

// Public forecast scoring uses the classical grahas found in BPHS, Horasara,
// Saravali, and Brihat Jataka. Outer planets may remain in raw ephemeris/config
// data for compatibility, but they do not drive the Vedic forecast scores.
export const CLASSICAL_GRAHAS = new Set([
  "sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu",
]);
export const CLASSICAL_TARGETS = new Set([
  ...CLASSICAL_GRAHAS,
  "ascendant", "descendant", "midheaven", "imumcoeli",
]);

// YAML weights still use simple soft/hard/conj buckets. These mappings let the
// old rule tables score Vedic graha drishti without Western orb aspects.
export const DRISHTI_ASPECT_ANGLE: Record<number, number> = {
  0: 0,
  3: 90,
  4: 90,
  5: 120,
  7: 180,
  8: 90,
  9: 120,
  10: 90,
};
export const DRISHTI_ASPECT_CLASS: Record<number, string> = {
  0: "conj",
  3: "hard",
  4: "hard",
  5: "soft",
  7: "hard",
  8: "hard",
  9: "soft",
  10: "hard",
};

// ---------- helpers: circular math ----------

export function norm_deg(x: number): number {
  return pymod(pymod(x, 360) + 360, 360);
}

export function sign_from_longitude(longitude: number): number {
  return Math.trunc(Math.floor(norm_deg(longitude) / 30));
}

export function whole_sign_house_center(asc_sign: number, house: number): number {
  const sign = pymod(asc_sign + house - 1, 12);
  return sign * 30.0 + 15.0;
}

/**
 * Return Vedic drishti house distance.
 * 0 means same-sign conjunction; otherwise 3/4/5/7/8/9/10 are aspectable.
 */
export function drishti_house_distance(transit_deg: number, target_deg: number): number {
  const transit_sign = sign_from_longitude(transit_deg);
  const target_sign = sign_from_longitude(target_deg);
  const sign_steps = pymod(target_sign - transit_sign, 12);
  if (sign_steps === 0) {
    return 0;
  }
  return sign_steps + 1;
}

/**
 * Return (aspect_angle_bucket, class, strength, house_distance) for a BPHS/Horasara drishti hit.
 */
export function drishti_hit(
  tplanet: string,
  target: string,
  tdeg: number,
  ndeg: number
): [number, string, number, number] | null {
  const tplanet_l = tplanet.toLowerCase();
  const target_l = target.toLowerCase();
  if (!CLASSICAL_GRAHAS.has(tplanet_l) || !CLASSICAL_TARGETS.has(target_l)) {
    return null;
  }

  const house_distance = drishti_house_distance(tdeg, ndeg);
  if (house_distance === 0) {
    return [0, "conj", 1.0, 0];
  }

  const [, strength] = get_drishti_strength(house_distance, tplanet_l);
  if (strength <= 0.0) {
    return null;
  }

  const aspect_angle = DRISHTI_ASPECT_ANGLE[house_distance];
  const acl = DRISHTI_ASPECT_CLASS[house_distance];
  return [aspect_angle, acl, strength, house_distance];
}

/** Accepts formats like "3°55′" or "3°55'30\"" but you already have decimal in dicts. */
export function dms_to_deg(dms_str: string): number {
  const s = dms_str.toLowerCase().replace(/ /g, "");
  let d = 0.0;
  if (s.includes("°")) {
    const parts = s.split("°");
    d += parseFloat(parts[0]);
    const rest = parts[1];
    let m = 0.0;
    if (rest.includes("′")) {
      m = parseFloat(rest.split("′")[0]);
    } else if (rest.includes("'")) {
      m = parseFloat(rest.split("'")[0]);
    }
    d += m / 60.0;
  }
  return d;
}

// Retrograde weighting (no speeds given; station not modeled here)
export function retro_weight(planet: string, retro_list: string[]): number {
  const p = planet.toLowerCase();
  if (!retro_list.includes(p)) {
    return 1.0;
  }
  // Slight salience bump; you can tweak per your data
  if (["jupiter", "saturn", "uranus", "neptune", "pluto", "chiron", "rahu", "ketu"].includes(p)) {
    return 1.15;
  }
  if (["mercury", "venus"].includes(p)) {
    return 1.05;
  }
  return 1.0;
}

export function tanh_scale(x: number, K = 2.5, cap = 5.0): number {
  return cap * Math.tanh(x / K);
}

// ---------- natal augmentation (angles that aren’t in dict) ----------

export function build_natal_angles(natal_deg: Record<string, number | null>): Record<string, number | null> {
  const out: Record<string, number | null> = { ...natal_deg };
  if ("ascendant" in natal_deg && natal_deg["ascendant"] !== null) {
    // Keep legacy target names, but make them whole-sign bhava proxies.
    // This avoids Western MC/IC geometry in the public Vedic forecast path.
    const asc_sign = sign_from_longitude(natal_deg["ascendant"]);
    out["descendant"] = whole_sign_house_center(asc_sign, 7);
    out["midheaven"] = whole_sign_house_center(asc_sign, 10);
    out["imumcoeli"] = whole_sign_house_center(asc_sign, 4);
  }
  return out;
}

// ---------- metric-specific sign rules (YAML‑driven) ----------

/**
 * Helper to match single value against rule condition.
 * Supports 'transit', 'target', 'aspect', and their *_in list variants.
 */
function _match_condition(val: any, cond: Record<string, any>, key: string): boolean {
  const single = cond[key];
  if (single !== undefined && single !== null && val !== single) {
    return false;
  }
  const multi = cond[`${key}_in`];
  if (multi !== undefined && multi !== null && !multi.includes(val)) {
    return false;
  }
  return true;
}

/** Match Vedic drishti conditions like 7, "7H", "OPP", or "CONJ". */
function _match_drishti_condition(house_distance: number | null, cond: Record<string, any>): boolean {
  if (!("drishti" in cond) && !("drishti_in" in cond)) {
    return true;
  }
  if (house_distance === null) {
    return false;
  }

  const values: any[] = [];
  if ("drishti" in cond) {
    values.push(cond["drishti"]);
  }
  values.push(...(cond["drishti_in"] ?? []));

  for (const value of values) {
    if (typeof value === "number" && house_distance === value) {
      return true;
    }
    if (typeof value === "string") {
      const alias = value.trim().toUpperCase();
      if (alias === "CONJ" && house_distance === 0) {
        return true;
      }
      if (alias === "OPP" && house_distance === 7) {
        return true;
      }
      if (alias.endsWith("H")) {
        const n = Number(alias.slice(0, -1));
        if (Number.isInteger(n) && house_distance === n) {
          return true;
        }
      }
    }
  }
  return false;
}

function _rule_condition_matches(
  cond: Record<string, any>,
  tplanet: string,
  target: string,
  aspect_angle: number,
  house_distance: number | null
): boolean {
  return (
    _match_condition(tplanet, cond, "transit") &&
    _match_condition(target, cond, "target") &&
    _match_condition(aspect_angle, cond, "aspect") &&
    _match_drishti_condition(house_distance, cond)
  );
}

function _weight_from_map(weights: Record<string, any>, aspect_angle: number, acl: string): number {
  if (aspect_angle === 180 && "opp" in weights) {
    return Number(weights["opp"]);
  }
  if (acl in weights) {
    return Number(weights[acl]);
  }
  const def = weights["default"];
  if (def !== undefined && def !== null) {
    return Number(def);
  }
  return 0.0;
}

function _weight_from_rules(
  rules: Array<Record<string, any>>,
  tplanet: string,
  target: string,
  aspect_angle: number,
  acl: string,
  house_distance: number | null
): number {
  for (const rule of rules) {
    const cond = rule["when"] ?? {};
    if (!_rule_condition_matches(cond, tplanet, target, aspect_angle, house_distance)) {
      continue;
    }
    return _weight_from_map(rule["weights"] ?? {}, aspect_angle, acl);
  }
  return 0.0;
}

/**
 * Generic sign rule: pull weights from YAML.
 */
export function yaml_sign_rule(
  metric: string,
  tplanet: string,
  target: string,
  aspect_angle: number,
  acl: string,
  house_distance: number | null = null
): number {
  const metric_cfg = _ASTRO_CONFIG["metrics"][metric];
  if (!metric_cfg) {
    return 0.0;
  }

  const rules = metric_cfg["rules"] ?? [];
  if (!rules.length) {
    return 0.0;
  }

  return _weight_from_rules(rules, tplanet, target, aspect_angle, acl, house_distance);
}

// keep old function name but delegate to YAML
export function sign_rule(
  metric: string,
  tplanet: string,
  target: string,
  a: number,
  acl: string,
  house_distance: number | null = null
): number {
  return yaml_sign_rule(metric, tplanet, target, a, acl, house_distance);
}

// ---------- metric → pairs (YAML‑driven) ----------

function _build_metric_pairs_from_config(): Record<string, Array<[string, string[]]>> {
  const out: Record<string, Array<[string, string[]]>> = {};
  for (const [metric_name, cfg] of Object.entries(_ASTRO_CONFIG["metrics"]) as Array<[string, any]>) {
    const pairs_cfg = cfg["pairs"] ?? [];
    const pairs_list: Array<[string, string[]]> = [];
    for (const pair of pairs_cfg) {
      const tplanet = (pair["transit"] as string).toLowerCase();
      const targets = ((pair["targets"] ?? []) as string[]).map((t) => t.toLowerCase());
      pairs_list.push([tplanet, targets]);
    }
    out[metric_name] = pairs_list;
  }
  return out;
}

export const METRIC_PAIRS = _build_metric_pairs_from_config();

export function compute_metrics(natal_chart: TransitChartData, todays_chart: TransitChartData): Record<string, any> {
  const natal_deg = build_natal_angles(natal_chart["deg"]);
  const transit_deg = todays_chart["deg"];
  const retro_list = (todays_chart["retrograde"] ?? []).map((p) => p.toLowerCase());

  // ensure ketu exists; if only rahu is provided
  if (!("ketu" in transit_deg) && "rahu" in transit_deg && transit_deg["rahu"] !== null) {
    transit_deg["ketu"] = norm_deg(transit_deg["rahu"] + 180.0);
  }

  const metrics_raw: Record<string, number> = Object.fromEntries(
    Object.keys(METRIC_PAIRS).map((k) => [k, 0.0])
  );

  for (const [metric, pairs] of Object.entries(METRIC_PAIRS)) {
    let total = 0.0;
    for (const [tplanet, targets] of pairs) {
      if (!(tplanet in transit_deg)) {
        continue;
      }
      const tdeg = transit_deg[tplanet] as number;
      for (const target of targets) {
        if (!(target in natal_deg)) {
          continue;
        }
        const ndeg = natal_deg[target] as number;
        total += score_pair(tplanet, target, tdeg, ndeg, retro_list, metric);
      }
    }
    metrics_raw[metric] = total;
  }

  // scale to ~[-5..+5] and add D1_ prefix
  const metrics_scaled: Record<string, any> = {};
  for (const [k, v] of Object.entries(metrics_raw)) {
    metrics_scaled[`D1_${k}`] = pythonRound(tanh_scale(v, 2.5, 5.0), 3);
  }
  return metrics_scaled;
}

// ---------- scoring kernel: BPHS/Horasara graha drishti ----------
/** Score a transit-to-natal pair using sign/house-based graha drishti. */
export function score_pair(
  tplanet: string,
  target: string,
  tdeg: number,
  ndeg: number,
  retro_list: string[],
  metric: string
): number {
  const tplanet_l = tplanet.toLowerCase();
  const target_l = target.toLowerCase();

  const hit = drishti_hit(tplanet_l, target_l, tdeg, ndeg);
  if (hit === null) {
    return 0.0;
  }

  const [aspect_angle, acl, strength, house_distance] = hit;
  const r_weight = retro_weight(tplanet_l, retro_list);
  const transit_importance = TRANSIT_WEIGHTS_BASE[tplanet_l] ?? 1.0;
  const sgn = sign_rule(metric, tplanet_l, target_l, aspect_angle, acl, house_distance);
  return strength * r_weight * transit_importance * sgn;
}

/**
 * Calculate divisional chart metrics for specific life areas.
 * Uses proper Vedic divisional calculations from _transit_data module.
 * YAML‑driven pairs and rules.
 */
export function get_divisional_data(
  natal_chart: TransitChartData,
  todays_chart: TransitChartData
): Record<string, number> {
  // Get natal positions
  const natal_deg = build_natal_angles(natal_chart["deg"]);
  const transit_deg = todays_chart["deg"];
  const retro_list = (todays_chart["retrograde"] ?? []).map((p) => p.toLowerCase());

  // Ensure ketu exists
  if (!("ketu" in transit_deg) && "rahu" in transit_deg && transit_deg["rahu"] !== null) {
    transit_deg["ketu"] = norm_deg(transit_deg["rahu"] + 180.0);
  }

  // Calculate divisional chart positions in the same varga space for natal and transit.
  /** Calculate divisional chart positions using proper Vedic rules */
  const calculate_varga = (degrees: Record<string, number | null>, division: number): Record<string, number | null> => {
    const varga: Record<string, number | null> = {};
    for (const [planet, deg] of Object.entries(degrees)) {
      if (deg === null) {
        varga[planet] = null;
        continue;
      }
      const [, div_lon] = get_divisional_position(deg, division);
      varga[planet] = div_lon;
    }
    return varga;
  };

  // Build all divisional vargas from YAML
  const divisional_cfg: Record<string, any> = _ASTRO_CONFIG["divisional_metrics"] ?? {};
  const natal_vargas_cache: Record<number, Record<string, number | null>> = {};
  const transit_vargas_cache: Record<number, Record<string, number | null>> = {};
  const divisional_metrics: Record<string, number> = {};

  for (const [metric_name, cfg] of Object.entries(divisional_cfg)) {
    const division = Math.trunc(Number(cfg["division"]));
    if (!(division in natal_vargas_cache)) {
      natal_vargas_cache[division] = calculate_varga(natal_deg, division);
    }
    if (!(division in transit_vargas_cache)) {
      transit_vargas_cache[division] = calculate_varga(transit_deg, division);
    }
    const natal_varga = natal_vargas_cache[division];
    const transit_varga = transit_vargas_cache[division];

    let total_score = 0.0;
    const pairs = cfg["pairs"] ?? [];
    const rules = cfg["rules"] ?? [];

    for (const pair of pairs) {
      const tplanet = (pair["transit"] as string).toLowerCase();
      const targets = ((pair["targets"] ?? []) as string[]).map((t) => t.toLowerCase());

      if (!(tplanet in transit_varga) || transit_varga[tplanet] === null) {
        continue;
      }
      const tdeg = transit_varga[tplanet] as number;
      const tplanet_l = tplanet;
      const r_weight = retro_weight(tplanet_l, retro_list);

      for (const target of targets) {
        if (!(target in natal_varga) || natal_varga[target] === null) {
          continue;
        }
        const varga_deg = natal_varga[target] as number;

        const hit = drishti_hit(tplanet_l, target, tdeg, varga_deg);
        if (hit === null) {
          continue;
        }
        const [aspect_angle, acl, strength, house_distance] = hit;
        const weight = _weight_from_rules(rules, tplanet_l, target, aspect_angle, acl, house_distance);
        total_score += strength * r_weight * weight;
      }
    }

    divisional_metrics[metric_name] = pythonRound(tanh_scale(total_score, 2.5, 5.0), 3);
  }

  return divisional_metrics;
}

/**
 * Calculate the Sudarshana annual bhava.
 * BPHS Ch. 74 assigns one year at a time to the 12 bhavas, starting
 * with the 1st bhava from birth, then cycling every 12 years.
 * Returns house number (1-12).
 */
export function calculate_profection_year(birth_dt: PlainDateTime, current_dt: PlainDateTime): number {
  const sidereal_year_days = 365.25636;
  const age = Math.trunc(diffSeconds(current_dt, birth_dt) / (sidereal_year_days * 24 * 60 * 60));
  const house = pymod(age, 12) + 1;
  return house;
}

// --- MOVE THESE TWO HELPERS UP, BEFORE compute_metrics_with_context ---

/**
 * Detect mutual receptions between natal and transiting planets.
 * Returns list of (planet1, planet2) tuples in mutual reception.
 */
export function detect_mutual_receptions(
  natal_deg: Record<string, number | null>,
  transit_deg: Record<string, number | null>
): Array<[string, string]> {
  const receptions: Array<[string, string]> = [];
  for (const [t_planet, t_lon] of Object.entries(transit_deg)) {
    if (["ascendant", "midheaven", "descendant", "imumcoeli", "rahu", "ketu"].includes(t_planet)) {
      continue;
    }
    if (t_lon === null) {
      continue;
    }
    const t_sign = Math.trunc(Math.floor(t_lon / 30));
    const t_ruler = RULERSHIPS[t_sign];
    if (!t_ruler || !(t_ruler in natal_deg) || natal_deg[t_ruler] === null) {
      continue;
    }
    const ruler_lon = natal_deg[t_ruler] as number;
    const ruler_sign = Math.trunc(Math.floor(ruler_lon / 30));
    const ruler_of_ruler = RULERSHIPS[ruler_sign];
    if (ruler_of_ruler === t_planet) {
      receptions.push([t_planet, t_ruler]);
    }
  }
  return receptions;
}

/**
 * Calculate how important a transit is based on planetary speed/nature.
 * Slower planets = more important transits.
 * Returns multiplier (0.5 to 3.0)
 *
 * NOTE: This function exists for potential future use (e.g., UI display, weighting
 * event detection) but transit importance is ALREADY applied in score_pair().
 */
export function calculate_transit_importance(
  planet: string,
  is_retrograde: boolean,
  is_stationing = false
): number {
  let base_weight = TRANSIT_WEIGHTS_BASE[planet.toLowerCase()] ?? 1.0;
  if (is_retrograde) {
    base_weight *= TRANSIT_WEIGHTS_MULTIPLIERS["retrograde"];
  }
  if (is_stationing) {
    base_weight *= TRANSIT_WEIGHTS_MULTIPLIERS["stationing"];
  }
  return base_weight;
}

// ---------------------------------------------------------------------

/**
 * Enhanced divisional calculation with dasha weighting and Sudarshana annual bhava context.
 */
export function get_divisional_data_with_context(
  natal_chart: TransitChartData,
  todays_chart: TransitChartData,
  birth_dt: PlainDateTime,
  current_dt: PlainDateTime,
  transit_obj: TransitData,
  timezone_offset = 5.5
): Record<string, number> {
  // Get base divisional metrics
  const divisional_metrics = get_divisional_data(natal_chart, todays_chart);

  // Get current dasha
  const dasha_info = transit_obj.get_current_dasha(birth_dt, current_dt, timezone_offset);
  const maha_lord = dasha_info["mahadasha"];
  const antar_lord = dasha_info["antardasha"];

  // Get Sudarshana annual bhava. Variable/config names retain "profection" for compatibility.
  const profection_house = calculate_profection_year(birth_dt, current_dt);

  // Apply annual-bhava emphasis to DIVISIONAL metrics
  if (profection_house in DIVISIONAL_PROFECTION_EMPHASIS) {
    for (const [metric, boost] of Object.entries(DIVISIONAL_PROFECTION_EMPHASIS[profection_house])) {
      if (metric in divisional_metrics) {
        divisional_metrics[metric] *= boost;
      }
    }
  }

  // Apply mahadasha offsets to divisional metrics
  if (maha_lord in DIVISIONAL_DASHA_OFFSETS) {
    for (const [metric, offset] of Object.entries(DIVISIONAL_DASHA_OFFSETS[maha_lord])) {
      if (metric in divisional_metrics) {
        divisional_metrics[metric] += offset;
      }
    }
  }

  // Apply antardasha offsets to divisional metrics
  if (antar_lord in DIVISIONAL_DASHA_OFFSETS) {
    for (const [metric, offset] of Object.entries(DIVISIONAL_DASHA_OFFSETS[antar_lord])) {
      if (metric in divisional_metrics) {
        const antar_offset = offset * ANTARDASHA_MULTIPLIER;
        divisional_metrics[metric] += antar_offset;
      }
    }
  }

  // FIXED: Apply house influences to DIVISIONAL metrics - use YAML config
  const natal_deg = build_natal_angles(natal_chart["deg"]);
  const house_positions = transit_obj.get_natal_house_positions(natal_deg);
  const planet_strengths: Record<string, number> = {};
  for (const [planet, lon] of Object.entries(natal_deg)) {
    if (lon !== null && !["ascendant", "midheaven", "descendant", "imumcoeli"].includes(planet)) {
      planet_strengths[planet] = transit_obj.calculate_planetary_strength(lon, planet);
    }
  }

  for (const [planet, strength] of Object.entries(planet_strengths)) {
    const house = house_positions[planet];
    if (house && house in DIVISIONAL_HOUSE_INFLUENCES) {
      for (const [metric, house_weight] of Object.entries(DIVISIONAL_HOUSE_INFLUENCES[house])) {
        if (metric in divisional_metrics) {
          const strength_modifier = 1.0 + (strength - 0.5) * STRENGTH_MODIFIER_RANGE;
          divisional_metrics[metric] = divisional_metrics[metric] * house_weight * strength_modifier;
        }
      }
    }
  }

  // Cap all divisional values at [-5, +5]
  for (const metric of Object.keys(divisional_metrics)) {
    if (typeof divisional_metrics[metric] === "number") {
      divisional_metrics[metric] = Math.max(-5.0, Math.min(5.0, pythonRound(divisional_metrics[metric], 3)));
    }
  }

  return divisional_metrics;
}

/**
 * Enhanced metrics calculation with natal context and dasha periods.
 */
export function compute_metrics_with_context(
  natal_chart: TransitChartData,
  todays_chart: TransitChartData,
  birth_dt: PlainDateTime,
  current_dt: PlainDateTime,
  transit_obj: TransitData,
  timezone_offset = 5.5
): Record<string, any> {
  // Get basic metrics
  const metrics = compute_metrics(natal_chart, todays_chart);

  // Get natal house positions
  const natal_deg = build_natal_angles(natal_chart["deg"]);
  const transit_deg = todays_chart["deg"];
  const house_positions = transit_obj.get_natal_house_positions(natal_deg);

  // Get planetary strengths
  const planet_strengths: Record<string, number> = {};
  for (const [planet, lon] of Object.entries(natal_deg)) {
    if (lon !== null && !["ascendant", "midheaven", "descendant", "imumcoeli"].includes(planet)) {
      planet_strengths[planet] = transit_obj.calculate_planetary_strength(lon, planet);
    }
  }

  // Get current dasha period
  const dasha_info = transit_obj.get_current_dasha(birth_dt, current_dt, timezone_offset);
  const maha_lord = dasha_info["mahadasha"];
  const antar_lord = dasha_info["antardasha"];

  // Get Sudarshana annual bhava. Variable/config names retain "profection" for compatibility.
  const profection_house = calculate_profection_year(birth_dt, current_dt);

  // Detect mutual receptions
  const mutual_receptions = detect_mutual_receptions(natal_deg, transit_deg);

  // REMOVED: transit_weights dict - transit importance already applied in score_pair()

  // Apply annual-bhava emphasis - ONLY D1 metrics
  if (profection_house in PROFECTION_EMPHASIS) {
    for (const [metric, boost] of Object.entries(PROFECTION_EMPHASIS[profection_house])) {
      if (metric in metrics) {
        metrics[metric] *= boost;
      }
    }
  }

  // Mutual reception bonus
  const mutual_reception_bonus = EVENT_DETECTION_CFG["mutual_reception_bonus"];
  if (mutual_receptions.length) {
    for (const metric of [
      "D1_Relationships", "D1_Career", "D1_Finance", "D1_Spiritual",
      "D1_Communication", "D1_Confidence", "D1_LearningIdeas", "D1_Mood",
    ]) {
      if (metric in metrics && metrics[metric] > 0) {
        metrics[metric] *= mutual_reception_bonus;
      }
    }
  }

  // Apply mahadasha offsets (full strength)
  if (maha_lord in DASHA_OFFSETS) {
    for (const [metric, offset] of Object.entries(DASHA_OFFSETS[maha_lord])) {
      if (metric in metrics) {
        metrics[metric] = metrics[metric] + offset;
      }
    }
  }

  // Apply antardasha offsets
  if (antar_lord in DASHA_OFFSETS) {
    for (const [metric, offset] of Object.entries(DASHA_OFFSETS[antar_lord])) {
      if (metric in metrics) {
        const antar_offset = offset * ANTARDASHA_MULTIPLIER;
        metrics[metric] = metrics[metric] + antar_offset;
      }
    }
  }

  // FIXED: Apply house context adjustments - use YAML config
  for (const [planet, strength] of Object.entries(planet_strengths)) {
    const house = house_positions[planet];
    if (house && house in HOUSE_INFLUENCES) {
      for (const [metric, house_weight] of Object.entries(HOUSE_INFLUENCES[house])) {
        if (metric in metrics) {
          const strength_modifier = 1.0 + (strength - 0.5) * STRENGTH_MODIFIER_RANGE;
          metrics[metric] = metrics[metric] * house_weight * strength_modifier;
        }
      }
    }
  }

  // Cap D1 values at [-5, +5]
  for (const key of Object.keys(metrics)) {
    if (typeof metrics[key] === "number") {
      metrics[key] = Math.max(-5.0, Math.min(5.0, pythonRound(metrics[key], 3)));
    }
  }

  // Store dasha data (renamed to capital case)
  metrics["Dasha_Maha"] = maha_lord;
  metrics["Dasha_Antar"] = antar_lord;

  return metrics;
}

/**
 * Detect significant astrological events based on multiple converging factors.
 * FIXED: More selective - require ALL conditions to be met, not just threshold checks.
 */
export function detect_major_transits(
  metrics: Record<string, any>,
  _natal_chart: TransitChartData,
  _todays_chart: TransitChartData,
  _current_dt: PlainDateTime
): string[] {
  const flags: string[] = [];
  const cfg = EVENT_DETECTION_CFG;

  // Helper to check if all conditions in a dict are met
  const meets_all_conditions = (condition_dict: Record<string, number>): boolean => {
    for (const [key, threshold] of Object.entries(condition_dict)) {
      const metric_val = metrics[key] ?? 0;
      if (key.endsWith("_abs")) {
        const actual_key = key.slice(0, -4);
        if (Math.abs(metrics[actual_key] ?? 0) <= threshold) {
          return false;
        }
      } else if (threshold > 0) {
        if (metric_val <= threshold) {
          return false;
        }
      } else {
        // negative threshold
        if (metric_val >= threshold) {
          return false;
        }
      }
    }
    return true;
  };

  // Career milestones - require ALL conditions
  if (meets_all_conditions(cfg["career"]["breakthrough"])) {
    flags.push("CAREER_BREAKTHROUGH");
  } else if (meets_all_conditions(cfg["career"]["peak"])) {
    flags.push("CAREER_PEAK");
  }

  if (meets_all_conditions(cfg["career"]["crisis"])) {
    flags.push("CAREER_CRISIS");
  } else if (meets_all_conditions(cfg["career"]["challenge"])) {
    flags.push("CAREER_CHALLENGE");
  }

  // Relationship events - require ALL conditions
  if (meets_all_conditions(cfg["relationships"]["breakthrough"])) {
    flags.push("RELATIONSHIP_BREAKTHROUGH");
  } else if (meets_all_conditions(cfg["relationships"]["opportunity"])) {
    flags.push("RELATIONSHIP_OPPORTUNITY");
  }

  if (meets_all_conditions(cfg["relationships"]["crisis"])) {
    flags.push("RELATIONSHIP_CRISIS");
  } else if (meets_all_conditions(cfg["relationships"]["tension"])) {
    flags.push("RELATIONSHIP_TENSION");
  }

  // Financial opportunities - require ALL conditions
  if (meets_all_conditions(cfg["finance"]["major_opportunity"])) {
    flags.push("MAJOR_FINANCIAL_OPPORTUNITY");
  } else if (meets_all_conditions(cfg["finance"]["opportunity"])) {
    flags.push("FINANCIAL_OPPORTUNITY");
  }

  if (meets_all_conditions(cfg["finance"]["caution"])) {
    flags.push("FINANCIAL_CAUTION");
  }

  // Transformation periods
  if (meets_all_conditions(cfg["transformation"]["major_positive"])) {
    flags.push("MAJOR_TRANSFORMATION_POSITIVE");
  } else if (meets_all_conditions(cfg["transformation"]["major_challenging"])) {
    flags.push("MAJOR_TRANSFORMATION_CHALLENGING");
  }

  if (meets_all_conditions(cfg["transformation"]["spiritual"])) {
    flags.push("SPIRITUAL_TRANSFORMATION");
  }

  // Health warnings - require ALL conditions
  if (meets_all_conditions(cfg["health"]["serious_caution"])) {
    flags.push("SERIOUS_HEALTH_CAUTION");
  } else if (meets_all_conditions(cfg["health"]["caution"])) {
    flags.push("HEALTH_CAUTION");
  }

  if (meets_all_conditions(cfg["health"]["vitality_peak"])) {
    flags.push("VITALITY_PEAK");
  }

  // Education/Learning - require ALL conditions
  if (meets_all_conditions(cfg["education"]["major_opportunity"])) {
    flags.push("MAJOR_EDUCATIONAL_OPPORTUNITY");
  } else if (meets_all_conditions(cfg["education"]["breakthrough"])) {
    flags.push("LEARNING_BREAKTHROUGH");
  }

  // Children/Creativity
  if (meets_all_conditions(cfg["creativity"]["peak"])) {
    flags.push("FERTILITY_CREATIVITY_PEAK");
  }

  // Authority/Leadership - require ALL conditions
  if (meets_all_conditions(cfg["leadership"]["recognition"])) {
    flags.push("LEADERSHIP_RECOGNITION");
  }

  // Innovation breakthrough - require ALL conditions
  if (meets_all_conditions(cfg["innovation"]["major"])) {
    flags.push("MAJOR_BREAKTHROUGH");
  } else if (meets_all_conditions(cfg["innovation"]["breakthrough"])) {
    flags.push("BREAKTHROUGH_PERIOD");
  }

  // Learning/expansion - require ALL conditions
  if (meets_all_conditions(cfg["learning"]["expansion"])) {
    flags.push("LEARNING_EXPANSION");
  }

  // Spiritual awakening - require ALL conditions
  if (meets_all_conditions(cfg["spiritual"]["awakening"])) {
    flags.push("SPIRITUAL_AWAKENING");
  } else if (meets_all_conditions(cfg["spiritual"]["growth"])) {
    flags.push("SPIRITUAL_GROWTH");
  }

  // Karmic events
  if (meets_all_conditions(cfg["karma"]["major_event"])) {
    flags.push("MAJOR_KARMIC_EVENT");
  }

  // Major life crossroads
  const intense_threshold = cfg["life_events"]["intense_threshold"];
  let intense_total = 0;
  let intense_positive = 0;
  let intense_negative = 0;
  for (const v of Object.values(metrics)) {
    if (typeof v === "number") {
      if (Math.abs(v) > intense_threshold) intense_total += 1;
      if (v > intense_threshold) intense_positive += 1;
      if (v < -intense_threshold) intense_negative += 1;
    }
  }

  if (intense_total >= cfg["life_events"]["major_pivot_count"]) {
    flags.push("MAJOR_LIFE_PIVOT");
  } else if (intense_total >= cfg["life_events"]["crossroads_count"]) {
    flags.push("MAJOR_LIFE_CROSSROADS");
  }

  if (intense_positive >= cfg["life_events"]["extremely_fortunate_count"]) {
    flags.push("EXTREMELY_FORTUNATE_PERIOD");
  }

  if (intense_negative >= cfg["life_events"]["highly_challenging_count"]) {
    flags.push("HIGHLY_CHALLENGING_PERIOD");
  }

  // Dasha-specific events - require ALL conditions
  const dasha_maha = metrics["Dasha_Maha"] ?? "";
  if (dasha_maha === cfg["dasha_events"]["saturn_maturation"]["dasha"]) {
    if (meets_all_conditions({ D1_Transformation: cfg["dasha_events"]["saturn_maturation"]["D1_Transformation"] })) {
      flags.push("SATURN_MATURATION_PERIOD");
    }
  }

  if (dasha_maha === cfg["dasha_events"]["rahu_disruption"]["dasha"]) {
    if (meets_all_conditions({ D1_Transformation_abs: cfg["dasha_events"]["rahu_disruption"]["D1_Transformation_abs"] })) {
      flags.push("RAHU_DISRUPTION_PERIOD");
    }
  }

  if (dasha_maha === cfg["dasha_events"]["jupiter_expansion"]["dasha"]) {
    if (meets_all_conditions({ D1_Confidence: cfg["dasha_events"]["jupiter_expansion"]["D1_Confidence"] })) {
      flags.push("JUPITER_EXPANSION_PERIOD");
    }
  }

  return flags;
}

/**
 * Get astrological predictions for a person.
 *
 * Args:
 *     current_datetime: datetime object (time component used only if mode='snapshot')
 *     person: person identifier from birth.json (REQUIRED — there is no default person)
 *     current_lat: current latitude (REQUIRED)
 *     current_lon: current longitude (REQUIRED)
 *     timezone_offset: hours ahead of UTC (REQUIRED)
 *     mode: 'snapshot' (single point in time) or 'daily_summary' (midpoint of daily range)
 *     override_seconds: ONLY for birth time rectification. Replaces birth seconds (0-59). Use -1 for no override.
 *     override_birth: FULL birth datetime override for rectification. Takes precedence over override_seconds.
 *     override_birth_info: birth.json person payload override for callers using a non-default birth file.
 *                          REQUIRED in the TS port (no config/birth.json file I/O in the browser).
 *
 * Returns:
 *     Dict with metrics. If daily_summary mode, returns (min+max)/2 for each metric.
 */
export function get_predictions(
  current_datetime: PlainDateTime,
  person: string,
  current_lat: number,
  current_lon: number,
  timezone_offset: number,
  mode = "daily_summary",
  override_seconds = -1,
  override_birth: PlainDateTime | null = null,
  override_birth_info: Record<string, any> | null = null
): Record<string, any> {
  let birth_info: Record<string, any>;
  if (override_birth_info !== null) {
    birth_info = override_birth_info;
  } else {
    // Python falls back to reading config/birth.json from disk here; the static
    // browser build has no filesystem, so fail loudly instead of returning {}.
    throw new Error(
      `get_predictions: override_birth_info is required in the TS port ` +
        `(config/birth.json file I/O is not available; person='${person}')`
    );
  }

  // FIXED: Handle override_birth parameter (takes precedence over override_seconds)
  let birth_datetime: PlainDateTime;
  let birth_lat: number;
  let birth_lon: number;
  if (override_birth !== null) {
    // Use the fully specified override birth datetime
    birth_datetime = override_birth;
    birth_lat = birth_info["lat"];
    birth_lon = birth_info["lon"];
  } else {
    // Original behavior: only override seconds if specified
    let seconds = birth_info["seconds"];
    if (override_seconds >= 0) {
      seconds = override_seconds;
    }

    birth_datetime = plainDateTime(
      birth_info["year"],
      birth_info["month"],
      birth_info["date"],
      birth_info["hour"],
      birth_info["minutes"],
      seconds
    );
    birth_lat = birth_info["lat"];
    birth_lon = birth_info["lon"];
  }

  const transit = new TransitData();

  if (mode === "snapshot") {
    // Single point in time - current_datetime is NOT affected by override_seconds
    const natal_chart = transit.get_transit_data(birth_datetime, birth_lat, birth_lon, timezone_offset);
    const todays_chart = transit.get_transit_data(current_datetime, current_lat, current_lon, timezone_offset);

    const metrics = compute_metrics_with_context(
      natal_chart, todays_chart, birth_datetime, current_datetime, transit, timezone_offset
    );
    const divisional = get_divisional_data_with_context(
      natal_chart, todays_chart, birth_datetime, current_datetime, transit, timezone_offset
    );
    Object.assign(metrics, divisional);

    const event_flags = detect_major_transits(metrics, natal_chart, todays_chart, current_datetime);
    metrics["Events"] = event_flags.length ? event_flags.join(",") : "";

    return metrics;
  } else if (mode === "daily_summary") {
    // Sample throughout the day and return midpoint (min+max)/2
    const base_date = combine(dateOnly(current_datetime), { hour: 0, minute: 0, second: 0 });

    const natal_chart = transit.get_transit_data(birth_datetime, birth_lat, birth_lon, timezone_offset);

    // Collect samples throughout the day (every 1 hour)
    const hourly_samples: Array<Record<string, any>> = [];
    for (let hour = 0; hour < 24; hour += 1) {
      const sample_time = combine(dateOnly(base_date), { hour, minute: 0, second: 0 });
      const todays_chart = transit.get_transit_data(sample_time, current_lat, current_lon, timezone_offset);

      const metrics = compute_metrics_with_context(
        natal_chart, todays_chart, birth_datetime, sample_time, transit, timezone_offset
      );
      const divisional = get_divisional_data_with_context(
        natal_chart, todays_chart, birth_datetime, sample_time, transit, timezone_offset
      );
      Object.assign(metrics, divisional);

      hourly_samples.push(metrics);
    }

    // Calculate midpoint (min+max)/2 for each metric
    const result: Record<string, any> = {};

    // Get all metric keys
    const metric_keys = Object.keys(hourly_samples[0]).filter(
      (k) =>
        (typeof hourly_samples[0][k] === "number" || ["Dasha_Maha", "Dasha_Antar"].includes(k)) &&
        !k.startsWith("_")
    );

    for (const metric_key of metric_keys) {
      if (["Dasha_Maha", "Dasha_Antar"].includes(metric_key)) {
        result[metric_key] = hourly_samples[6][metric_key];
      } else {
        const values = hourly_samples.map((sample) => sample[metric_key] as number);
        const min_val = Math.min(...values);
        const max_val = Math.max(...values);
        const midpoint = (min_val + max_val) / 2.0;
        result[metric_key] = pythonRound(midpoint, 3);
      }
    }

    // Detect events based on midpoint values
    const noon_transit = transit.get_transit_data(
      combine(dateOnly(base_date), { hour: 12, minute: 0, second: 0 }),
      current_lat,
      current_lon,
      timezone_offset
    );

    const event_metrics = { ...result };
    const event_flags = detect_major_transits(
      event_metrics,
      natal_chart,
      noon_transit,
      combine(dateOnly(base_date), { hour: 12, minute: 0, second: 0 })
    );
    result["Events"] = event_flags.length ? event_flags.join(",") : "";

    return result;
  } else {
    throw new Error(`Unknown mode: ${mode}. Use 'snapshot' or 'daily_summary'`);
  }
}
